if (!customElements.get('campaign-hero')) {
  class CampaignHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.touchStart = null;

      if (this.slides.length < 2) return;

      this.querySelector('[data-hero-previous]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.heroDot);
          this.show(index, index >= this.currentIndex ? 1 : -1);
        });
      });

      this.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      }, { passive: true });

      this.addEventListener('touchend', (event) => {
        if (!this.touchStart) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStart.x;
        const deltaY = touch.clientY - this.touchStart.y;
        this.touchStart = null;

        if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.go(deltaX < 0 ? 1 : -1);
        }
      }, { passive: true });

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });

      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-hero-slide]');
        if (!slide) return;
        this.stopAutoplay();
        const index = Number(slide.dataset.slideIndex);
        this.show(index, index >= this.currentIndex ? 1 : -1);
      });

      this.startAutoplay();
    }

    go(direction) {
      const nextIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
      this.show(nextIndex, direction);
    }

    show(index, direction = 1) {
      if (index === this.currentIndex || !this.slides[index]) return;

      const previous = this.slides[this.currentIndex];
      const next = this.slides[index];
      previous.hidden = true;
      previous.setAttribute('aria-hidden', 'true');

      next.hidden = false;
      next.setAttribute('aria-hidden', 'false');
      next.classList.remove('is-from-next', 'is-from-previous');
      void next.offsetWidth;
      next.classList.add(direction > 0 ? 'is-from-next' : 'is-from-previous');

      this.currentIndex = index;
      this.style.setProperty('--active-claim-color', next.dataset.slideColor);
      this.style.setProperty('--active-claim-text', next.dataset.slideTextColor);
      this.dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-pressed', String(dotIndex === index));
      });

      this.classList.remove('is-switching');
      void this.offsetWidth;
      this.classList.add('is-switching');
      window.setTimeout(() => this.classList.remove('is-switching'), 650);
      this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || this.reducedMotion || document.hidden || this.slides.length < 2) return;
      this.autoplayTimer = window.setTimeout(() => this.go(1), this.autoplaySpeed);
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimer);
    }
  }

  customElements.define('campaign-hero', CampaignHero);
}

if (!customElements.get('distrib-campaign')) {
  class DistribCampaign extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-campaign-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-campaign-panel]'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.select(tab.dataset.campaignTab, {
            scrollOnMobile: true,
            transitionOnDesktop: true
          });
          tab.focus({ preventScroll: true });
        });
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[data-campaign-panel]');
        if (panel) this.select(panel.dataset.campaignPanel);
      });
    }

    select(id, options = {}) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.campaignTab === id;
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      const revealPanel = () => {
        let activePanel;

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.campaignPanel === id;
          panel.hidden = !isActive;
          if (isActive) activePanel = panel;
        });

        this.classList.remove('is-changing');
        void this.offsetWidth;
        this.classList.add('is-changing');
        window.clearTimeout(this.animationTimer);
        this.animationTimer = window.setTimeout(() => this.classList.remove('is-changing'), 780);

        if (options.scrollOnMobile && window.matchMedia('(max-width: 989px)').matches && activePanel) {
          window.requestAnimationFrame(() => {
            activePanel.scrollIntoView({
              behavior: this.reducedMotion ? 'auto' : 'smooth',
              block: 'start'
            });
          });
        }
      };

      if (
        options.transitionOnDesktop
        && !this.reducedMotion
        && window.matchMedia('(min-width: 990px)').matches
      ) {
        const activeTab = this.tabs.find((tab) => tab.dataset.campaignTab === id);
        const flightColor = activeTab
          ? getComputedStyle(activeTab).getPropertyValue('--segment-color').trim()
          : '';

        if (flightColor) this.style.setProperty('--flight-color', flightColor);
        window.clearTimeout(this.transitionTimer);
        window.clearTimeout(this.transitionCleanupTimer);
        this.classList.remove('is-launching');
        void this.offsetWidth;
        this.classList.add('is-launching');
        this.transitionTimer = window.setTimeout(revealPanel, 640);
        this.transitionCleanupTimer = window.setTimeout(() => {
          this.classList.remove('is-launching');
        }, 1500);
      } else {
        revealPanel();
      }
    }

    onKeydown(event, currentIndex) {
      const handledKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!handledKeys.includes(event.key)) return;

      event.preventDefault();
      let nextIndex = currentIndex;

      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      }

      const nextTab = this.tabs[nextIndex];
      this.select(nextTab.dataset.campaignTab);
      nextTab.focus();
    }
  }

  customElements.define('distrib-campaign', DistribCampaign);
}
